import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, RefreshCw, Bell, Inbox } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * AdminAlertsPanel - surface + acknowledge UI for `db.admin_alerts`.
 *
 * Currently sourced from the spotlight deliverability canary, but generic
 * across any alert kind. Shows unacknowledged alerts first; admins can ack
 * each row individually or mark all as read in one click.
 */
const fmt = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString('en-US', { timeZone: 'America/Chicago' }); } catch { return iso; }
};

const severityStyles = {
  warning: { box: 'bg-amber-50 border-amber-200', text: 'text-amber-900', icon: 'text-amber-600' },
  error:   { box: 'bg-red-50 border-red-200', text: 'text-red-900', icon: 'text-red-600' },
  info:    { box: 'bg-sky-50 border-sky-200', text: 'text-sky-900', icon: 'text-sky-600' },
};

const AdminAlertsPanel = () => {
  const [alerts, setAlerts] = useState(null);
  const [unread, setUnread] = useState(0);
  const [showAcked, setShowAcked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = async (includeAcked = showAcked) => {
    setErr(null);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/alerts?include_acked=${includeAcked ? 'true' : 'false'}`,
        { credentials: 'include' },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setAlerts(d.alerts || []);
      setUnread(d.unread_count || 0);
    } catch (e) {
      setErr(String(e.message || e));
      setAlerts([]);
    }
  };

  useEffect(() => { load(showAcked); /* eslint-disable-next-line */ }, [showAcked]);

  const ackOne = async (id) => {
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/admin/alerts/${id}/acknowledge`, {
        method: 'POST', credentials: 'include',
      });
      await load(showAcked);
    } finally {
      setBusy(false);
    }
  };

  const ackAll = async () => {
    if (!window.confirm('Mark all unacknowledged alerts as read?')) return;
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/admin/alerts/acknowledge-all`, {
        method: 'POST', credentials: 'include',
      });
      await load(showAcked);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-[#FFFFFF] border border-[#F4ECD8] p-6"
      data-testid="admin-alerts-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="inline-flex items-center gap-2 mb-1">
            <Bell className="w-4 h-4 text-[#1A5C6E]" />
            <h3 className="font-semibold text-[#0A1F2E]">System alerts</h3>
            {unread > 0 && (
              <span
                className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[10px] font-bold"
                data-testid="admin-alerts-unread-badge"
              >
                {unread}
              </span>
            )}
          </div>
          <p className="text-xs text-[#5A6B73] max-w-md">
            Deliverability canaries, broadcast failures, and any other automated alarms raised by Mundalo background jobs.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 text-xs text-[#5A6B73] select-none" data-testid="admin-alerts-show-acked-toggle">
            <input
              type="checkbox"
              checked={showAcked}
              onChange={(e) => setShowAcked(e.target.checked)}
              className="accent-[#2CA8A0]"
            />
            Show acknowledged
          </label>
          <button
            type="button"
            onClick={ackAll}
            disabled={busy || unread === 0}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-mono uppercase tracking-wider font-semibold bg-[#2CA8A0] text-white rounded-full hover:brightness-95 transition disabled:opacity-50"
            data-testid="admin-alerts-ack-all"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            Mark all read
          </button>
          <button
            type="button"
            onClick={() => load(showAcked)}
            className="p-2 text-[#0A1F2E]/60 hover:text-[#0A1F2E]"
            title="Refresh"
            data-testid="admin-alerts-refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {err && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded" data-testid="admin-alerts-error">
          {err}
        </div>
      )}

      {alerts === null ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 bg-[#F4ECD8] animate-pulse rounded" />
          ))}
        </div>
      ) : alerts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center" data-testid="admin-alerts-empty">
          <Inbox className="w-8 h-8 text-[#2CA8A0]/70 mb-2" />
          <p className="text-sm text-[#5A6B73]">
            {showAcked ? 'No alerts on record.' : 'No unacknowledged alerts. You are all caught up.'}
          </p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="admin-alerts-list">
          {alerts.map((a) => {
            const sev = severityStyles[a.severity] || severityStyles.warning;
            const acked = !!a.acknowledged;
            return (
              <li
                key={a.id}
                className={`p-3 border rounded-lg ${acked ? 'bg-[#F9F6EF] border-[#F4ECD8] opacity-70' : sev.box}`}
                data-testid={`admin-alert-${a.id}`}
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${acked ? 'text-[#5A6B73]' : sev.icon}`} />
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm ${acked ? 'text-[#0A1F2E]/70' : sev.text}`}>
                      <span className="font-mono uppercase text-[10px] tracking-widest mr-2">
                        {a.kind || 'alert'}
                      </span>
                      {a.message || '(no message)'}
                    </div>
                    {a.metadata && (
                      <div className="mt-1 text-[11px] font-mono text-[#5A6B73] truncate">
                        {Object.entries(a.metadata)
                          .filter(([, v]) => v !== null && v !== undefined && v !== '')
                          .map(([k, v]) => `${k}=${typeof v === 'number' ? (Math.round(v * 100) / 100) : String(v).slice(0, 80)}`)
                          .join(' · ')}
                      </div>
                    )}
                    <div className="mt-1 text-[11px] text-[#5A6B73]">
                      {fmt(a.created_at)}
                      {acked && a.acknowledged_at && ` · acked ${fmt(a.acknowledged_at)}`}
                    </div>
                  </div>
                  {!acked && (
                    <button
                      type="button"
                      onClick={() => ackOne(a.id)}
                      disabled={busy}
                      className="text-xs font-mono uppercase tracking-wider text-[#1A5C6E] hover:text-[#2CA8A0] disabled:opacity-50 flex-shrink-0"
                      data-testid={`admin-alert-ack-${a.id}`}
                    >
                      Acknowledge
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default AdminAlertsPanel;
